// =====================================================================
// Fase operacional da viagem — o eixo da Torre de Controle.
//
// A torre pergunta três coisas ao olhar para o dia:
//   o que está LIBERADO pelo cliente e ainda precisa ser programado?
//   quem já está agendado / em viagem?
//   o que já carregamos e entregamos?
//
// Isso NÃO é o mesmo que `status_carga`. O status é um campo digitado, e
// 554 das 2.506 viagens VEGA (e a maior parte das USIMINAS) o deixam em
// branco — a planilha simplesmente parou de preencher a coluna. Uma aba
// que filtrasse `status = 'LIBERADO'` esconderia um quinto da operação.
//
// A fase é DERIVADA, e usa o fato operacional que a torre mantém sempre
// atualizado: TER MOTORISTA VINCULADO é o que separa "ainda a programar"
// de "já programado".
//
// CONFIRMADO PELA OPERAÇÃO em 18/08/2026: carga com motorista vinculado já
// conta como agendada, mesmo sem o status ter sido trocado. Não é um atalho
// técnico — é a leitura de quem usa a tela. (Ver docs/TORRE_CONTROLE.md §2.)
//
// Espelhada em ops.f_fase_viagem() (migration 018 §4). As duas precisam
// continuar iguais — a SQL filtra e conta no banco, esta versão rotula e
// reagrupa na tela.
// =====================================================================

export const FASES = ["DISPONIVEL", "AGENDADA", "FINALIZADA"] as const;
export type Fase = (typeof FASES)[number];

export const EMPRESAS = ["VEGA", "USIMINAS", "CL"] as const;
export type Empresa = (typeof EMPRESAS)[number];

// A planilha gravava "ADICIONAR MOTORISTA" (o texto-modelo da linha 2 da
// FROTA) no lugar de deixar a célula vazia. Para a operação isso significa
// "ainda não vinculado" — igual a vazio.
const SEM_MOTORISTA = new Set(["", "-", "--", "ADICIONAR MOTORISTA", "SEM MOTORISTA", "SEM VÍNCULO", "SEM VINCULO"]);

/** Um motorista de verdade está vinculado a esta viagem? */
export function temMotorista(motorista: unknown): boolean {
  return !SEM_MOTORISTA.has(String(motorista ?? "").trim().toUpperCase());
}

export interface ViagemFaseInput {
  status_carga?: unknown;
  motorista?: unknown;
}

/**
 * Fase operacional da viagem.
 *
 *   FINALIZADA  status = CARREGADO         → já carregamos/entregamos
 *   AGENDADA    status = AGENDADO, OU tem motorista vinculado
 *   DISPONIVEL  o resto                    → liberado pelo cliente, a programar
 *
 * A ordem importa: uma viagem CARREGADA continua tendo motorista, então o
 * teste de "finalizada" precisa vir antes do de "tem motorista".
 */
export function faseViagem(v: ViagemFaseInput): Fase {
  const status = String(v.status_carga ?? "").trim().toUpperCase();
  if (status === "CARREGADO") return "FINALIZADA";
  if (status === "AGENDADO") return "AGENDADA";
  if (temMotorista(v.motorista)) return "AGENDADA";
  return "DISPONIVEL";
}

export interface FaseMeta {
  /** Rótulo da aba (concorda com "cargas": "cargas disponíveis"). */
  label: string;
  /** Frase curta que explica a aba para quem abre a tela pela 1ª vez. */
  descricao: string;
  /** Classes Tailwind do chip/aba (mesma paleta dos Badges de status). */
  tone: string;
  /** Cor da barra de proporção no cartão da empresa. */
  bar: string;
}

export const FASE_META: Record<Fase, FaseMeta> = {
  DISPONIVEL: {
    label: "Disponíveis",
    descricao: "Liberadas pelo cliente e ainda sem motorista — é o que há para programar.",
    tone: "bg-blue-100 text-blue-700",
    bar: "bg-blue-400",
  },
  AGENDADA: {
    label: "Agendadas",
    descricao: "Motorista vinculado: agendadas e em viagem, até o carregamento ser confirmado.",
    tone: "bg-amber-100 text-amber-700",
    bar: "bg-amber-400",
  },
  FINALIZADA: {
    label: "Finalizadas",
    descricao: "Carga já carregada e entregue.",
    tone: "bg-green-100 text-green-700",
    bar: "bg-green-500",
  },
};

/** Rótulo da empresa contratante (quem paga o frete, não quem recebe a carga). */
export const EMPRESA_META: Record<Empresa, { label: string; descricao: string }> = {
  VEGA: { label: "VEGA", descricao: "Operação VEGA — programação via ledger DATA" },
  USIMINAS: { label: "USIMINAS", descricao: "Operação USIMINAS — cargas das usinas (Cubatão, Ipatinga, Betim…)" },
  CL: { label: "CL", descricao: "Operação CL / Sampaio" },
};

export function isFase(v: unknown): v is Fase {
  return typeof v === "string" && (FASES as readonly string[]).includes(v);
}

export function isEmpresa(v: unknown): v is Empresa {
  return typeof v === "string" && (EMPRESAS as readonly string[]).includes(v);
}
